import { Component, Input, OnInit } from '@angular/core';
import { ControlContainer } from '@angular/forms';
import { DateService } from '@hospitality-bot/shared/utils';
import { FormComponent } from '../form.components';
import { debounceTime, switchMap, tap } from 'rxjs/operators';

@Component({
  selector: 'hospitality-bot-date',
  templateUrl: './date.component.html',
  styleUrls: ['./date.component.scss'],
})
export class DateComponent extends FormComponent implements OnInit {
  @Input() infoButtonText: string;
  dateValue: Date;

  /* Default Date Settings */
  enableTime = true;
  isTimeOnly = false;
  showIcon = true;
  enableSeconds = false;
  hourFormat: 12 | 24 = 12;
  dateFormat = 'd/m/yy';
  readonlyInput = true;
  enableButtonBar = false;
  yearRange = '2000:2030';
  view = 'date';

  /**
   * @Input to change default date setting
   */
  @Input() set settings(value: DateSetting) {
    Object.entries(value)?.forEach(([key, value]) => {
      this[key] = value;
    });
  }

  /* To add Min and max date validation */
  @Input() minDate: Date;
  @Input() maxDate: Date;

  constructor(public controlContainer: ControlContainer) {
    super(controlContainer);
  }

  @Input() set resetDate(value: boolean) {
    value && this.onResetDate();
  }

  ngOnInit(): void {
    this.initInputControl();

    this.setDateValue(this.inputControl.value);

    this.inputControl.valueChanges.subscribe((res) => {
      this.setDateValue(res);
    });
  }

  private setDateValue(res: any) {
    if (!res) {
      this.dateValue = null;
      return;
    }
    let time: number;
    if (res instanceof Date) {
      time = res.getTime();
    } else if (typeof res === 'number') {
      time = res;
    } else if (typeof res === 'string' && !isNaN(Number(res.trim()))) {
      time = Number(res.trim());
    } else {
      time = new Date(res).getTime();
    }

    if (isNaN(time)) {
      this.dateValue = null;
      return;
    }

    const newDate = new Date(time);
    if (!this.dateValue || this.dateValue.getTime() !== newDate.getTime()) {
      this.dateValue = newDate;
    }
  }

  get modIconNgClasses() {
    return {
      ...this.iconNgClass,
      'pi-spin pi-spinner': this.isLoading,
      'pi-calendar': !this.isLoading,
    };
  }

  /**
   * @function onDateChange to handle the changes
   */
  onDateChange(date: Date) {
    if (!date) {
      // if date is null, reset the inputControl and dateValue
      this.inputControl.setValue(null);
      this.dateValue = null;
      return;
    }

    const epochDate = new Date(date).getTime();
    const currentTime = this.inputControl.value;
    const currentNumTime =
      typeof currentTime === 'number'
        ? currentTime
        : typeof currentTime === 'string' && !isNaN(Number(currentTime.trim()))
        ? Number(currentTime.trim())
        : new Date(currentTime).getTime();

    // Only setValue if it is actually changed
    if (currentNumTime !== epochDate) {
      this.inputControl.setValue(epochDate);
    }
    this.inputControl.markAsDirty();
  }

  onResetDate() {
    this.inputControl.setValue(null);
    this.dateValue = null;
  }

  /**
   * @function markAsTouched to mark date input as touched
   */
  markAsTouched() {
    this.inputControl.markAsTouched();
  }
}

/**
 * @type DateSetting
 * @property translateKey Translation key whose value will be fetched from translation files.
 * @property readonlyInput To disable setting date using user input.
 * @property enableButtonBar To add cta to calendar
 *
 */
export type DateSetting = {
  isTimeOnly: boolean;
  showIcon: boolean;
  enableTime: boolean;
  enableSeconds: boolean;
  hourFormat: 12 | 24;
  dateFormat: string;
  readonlyInput: boolean;
  enableButtonBar: boolean;
  dateValue: Date;
  isDisabled: boolean;
  yearRange: string;
};

/**
 * Date Format
 * d - day of month (no leading zero)
 * dd - day of month (two digit)
 * o - day of the year (no leading zeros)
 * oo - day of the year (three digit)
 * D - day name short
 * DD - day name long
 * m - month of year (no leading zero)
 * mm - month of year (two digit)
 * M - month name short
 * MM - month name long
 * y - year (two digit)
 * yy - year (four digit)
 * @ - Unix timestamp (ms since 01/01/1970)
 * ! - Windows ticks (100ns since 01/01/0001)
 * '...' - literal text
 * '' - single quote
 * anything else - literal text
 */
